import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { useMemo } from 'react';
import useSWRInfinite, { type SWRInfiniteResponse } from 'swr/infinite';

const PAGE_SIZE = 100;

type Page = { skip: number; limit: number };

// Loads a list page by page, refreshes it every 30s, and merges the pages by item id.
export function usePaginatedList<Item extends { id: { toHexString(): string } }, Params>(
  resource: string,
  params: Params,
  fetchPage: (params: Params, page: Page) => Promise<Array<Item>>,
): {
  items: Array<Item> | undefined;
  isLoading: boolean;
  error: Error;
  size: number;
  setSize: (size: number) => void;
  mutate: SWRInfiniteResponse<Array<Item>, Error>['mutate'];
} {
  const getKey = (
    pageIndex: number,
    previousPageData: Array<Item> | null,
  ): [string, string, Params, number, number] | null => {
    if (previousPageData && !previousPageData.length) return null;
    return ['GET', resource, params, pageIndex * PAGE_SIZE, PAGE_SIZE];
  };

  const { data, isLoading, error, size, setSize, mutate } = useSWRInfinite(
    getKey,
    ([, , params, skip, limit]) => fetchPage(params, { skip, limit }),
    {
      revalidateFirstPage: false,
      refreshInterval: OneSecondInMillis * 30,
      revalidateAll: false,
    },
  );

  const items = useMemo(() => {
    if (data == null) return undefined;
    const byId = new Map<string, Item>(data.flat().map((item) => [item.id.toHexString(), item]));
    return Array.from(byId.values());
  }, [data]);

  return { items, isLoading, error, size, setSize, mutate };
}
